/**
 * Cross-locale fallbacks for composed property room wire data.
 */

/**
 * @param {unknown} primary
 * @param {unknown} fallback
 * @returns {string}
 */
export function pickTrimmedString(primary, fallback) {
  const p = primary != null ? String(primary).trim() : '';
  if (p) return p;
  return fallback != null ? String(fallback).trim() : '';
}

/**
 * Public-facing room title for admin UI: localized label with default-locale fallback, then canonical name.
 *
 * @param {object} opts
 * @param {string|number|null|undefined} opts.roomId
 * @param {unknown} opts.roomName
 * @param {Record<string, Record<string, { label?: string }>>|null|undefined} opts.allRoomLocales
 * @param {string|null|undefined} opts.locale
 * @param {string|null|undefined} opts.defaultLocale
 * @returns {string}
 */
export function resolveRoomDisplayLabel({
  roomId,
  roomName,
  allRoomLocales,
  locale,
  defaultLocale
} = {}) {
  const id = roomId != null ? String(roomId) : '';
  const bucket = (id && allRoomLocales?.[id]) || (roomId != null && allRoomLocales?.[roomId]) || {};
  const activeLocale = locale || defaultLocale || '';
  const primary = bucket[activeLocale]?.label;
  const fallback =
    activeLocale && defaultLocale && activeLocale !== defaultLocale
      ? bucket[defaultLocale]?.label
      : '';
  const localized = pickTrimmedString(primary, fallback);
  if (localized) return localized;
  return pickTrimmedString(roomName, '');
}

/**
 * Admin floor-plan preview for one locale. Empty stays empty — no default-locale
 * or canonical room-id fallback (those made every language look like the room ID).
 *
 * @param {object} opts
 * @param {string|null|undefined} opts.locale
 * @param {Record<string, string>|null|undefined} opts.liveLabels
 * @param {Record<string, { label?: string }>|null|undefined} opts.storedLabels
 * @returns {string}
 */
export function resolveAdminRoomPlanLabel({ locale, liveLabels, storedLabels } = {}) {
  const loc = locale || '';
  if (!loc) return '';
  const live = liveLabels?.[loc];
  if (live != null && String(live).trim()) return String(live).trim();
  return pickTrimmedString(storedLabels?.[loc]?.label, '');
}

/**
 * @param {object[]|null|undefined} images
 * @param {object[]|null|undefined} fallbackImages
 * @returns {object[]}
 */
export function mergeRoomImagesWithLocaleFallback(images, fallbackImages) {
  const primary = Array.isArray(images) ? images : [];
  const fallback = Array.isArray(fallbackImages) ? fallbackImages : [];
  return primary.map((img, index) => {
    const fb = fallback[index] || {};
    return {
      ...img,
      alt: pickTrimmedString(img?.alt, fb.alt),
      caption: pickTrimmedString(img?.caption, fb.caption)
    };
  });
}

/**
 * @param {object|null|undefined} room
 * @param {object|null|undefined} fallbackRoom
 * @returns {object|null|undefined}
 */
export function mergeRoomWithLocaleFallback(room, fallbackRoom) {
  if (!room) return room;
  if (!fallbackRoom) return room;
  return {
    ...room,
    description: pickTrimmedString(room.description, fallbackRoom.description),
    images: mergeRoomImagesWithLocaleFallback(room.images, fallbackRoom.images)
  };
}

/**
 * Fill empty per-locale room copy from fallback floors (matched by room id).
 *
 * @param {object[]|null|undefined} floors
 * @param {object[]|null|undefined} fallbackFloors
 * @returns {object[]}
 */
export function mergeFloorsWithRoomLocaleFallback(floors, fallbackFloors) {
  if (!Array.isArray(floors)) return [];

  const fallbackByRoomId = new Map();
  for (const floor of Array.isArray(fallbackFloors) ? fallbackFloors : []) {
    for (const room of Array.isArray(floor?.rooms) ? floor.rooms : []) {
      const id = room?.id != null ? String(room.id) : '';
      if (id) fallbackByRoomId.set(id, room);
    }
  }

  return floors.map((floor) => ({
    ...floor,
    rooms: (Array.isArray(floor?.rooms) ? floor.rooms : []).map((room) =>
      mergeRoomWithLocaleFallback(room, fallbackByRoomId.get(String(room?.id)))
    )
  }));
}
